import Checkbox from '../../../Checkbox';
import type { SelectOption } from '../../../Select';
import Legend from '../../../../charts/Legend';
import { getDefaultChartColor } from '../../../../helpers/colors';

import AddFacetSelect from '../AddFacetSelect';
import ValueSelectList from '../ValueSelectList';

import { MAX_COMPARE_VALUES, NO_COMPARE_VALUE } from '../../utilities/constants';
import {
  automaticallyIncludesAllCompareValues,
  buildFacetGroups,
  facetById,
  truncate,
} from '../../utilities/helpers';
import type { CompareBy, Facet } from '../../utilities/types';

import styles from './_index.module.scss';

export type CompareByPickerProps = {
  facets: Facet[];
  onChange: (compareBy: CompareBy | null) => void;
  value: CompareBy | null;
};

const defaultCompareByFor = (facet: Facet): CompareBy => ({
  facetId: facet.id,
  includeAllOthers: false,
  selectedValues: automaticallyIncludesAllCompareValues(facet)
    ? facet.options.map(({ value }) => value)
    : [],
});

/**
 * Compare By picker. A grouped Select (with a leading "Overall") chooses the
 * cut. Demographic and quota facets include every value automatically; other
 * facets expose a value-picker capped at MAX_COMPARE_VALUES.
 */
const CompareByPicker = ({ facets, onChange, value }: CompareByPickerProps) => {
  const groups = buildFacetGroups(facets, { predicate: (facet) => facet.comparable });
  const leadingOption: SelectOption = { label: 'Overall', value: NO_COMPARE_VALUE };

  const selectedFacet = value ? facetById(facets, value.facetId) : undefined;
  const includesAllValues = selectedFacet && automaticallyIncludesAllCompareValues(selectedFacet);
  const includedOptionLegendItems = includesAllValues
    ? selectedFacet.options.map(({ label }, index) => ({
        color: getDefaultChartColor(index),
        name: label,
      }))
    : [];
  const selectedOption: SelectOption = selectedFacet
    ? { label: truncate(selectedFacet.label), value: selectedFacet.id }
    : leadingOption;

  const handleSelect = (facetId: string) => {
    if (!facetId) return onChange(null);
    const facet = facetById(facets, facetId);
    if (facet) onChange(defaultCompareByFor(facet));
  };

  return (
    <section className={styles['compare-by']} data-testid="global-filters-compare-by">
      <h4 className={styles['title']}>Compare by</h4>
      <AddFacetSelect
        aria-label="Compare by"
        data-testid="global-filters-compare-select"
        groups={groups}
        leadingOption={leadingOption}
        onSelect={handleSelect}
        placeholder="Overall"
        value={selectedOption}
      />

      {value && selectedFacet && (
        <div className={styles['values']}>
          {includesAllValues ? (
            <>
              <span className={styles['values-help']}>All options included</span>
              <Legend
                className={styles['options-legend']}
                isHorizontal={false}
                isUpdating={false}
                items={includedOptionLegendItems}
              />
            </>
          ) : (
            <>
              <div className={styles['values-header']}>
                <span className={styles['values-help']}>
                  Pick up to {MAX_COMPARE_VALUES} ({value.selectedValues.length}/
                  {MAX_COMPARE_VALUES})
                </span>
                <Checkbox
                  aria-label='Include "All Others"'
                  id={`compare-all-others-${selectedFacet.id}`}
                  isChecked={value.includeAllOthers ?? false}
                  label='Include "All Others"'
                  onChange={() =>
                    onChange({ ...value, includeAllOthers: !(value.includeAllOthers ?? false) })
                  }
                  value="all-others"
                />
              </div>
              <ValueSelectList
                idPrefix={`compare-${selectedFacet.id}`}
                key={selectedFacet.id}
                maxSelected={MAX_COMPARE_VALUES}
                onChange={(selectedValues) => onChange({ ...value, selectedValues })}
                options={selectedFacet.options}
                searchPlaceholder={
                  selectedFacet.category === 'question'
                    ? 'Search answer options…'
                    : `Search ${selectedFacet.label.toLowerCase()}…`
                }
                selectedValues={value.selectedValues}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default CompareByPicker;
